<main>
  <loading-overlay [isLoading]="isLoading() > 0" [fullWidth]="true"></loading-overlay>
  <p-toast position="bottom-right" key="br"></p-toast>

  <header>
    <div class="headline row-start-center gap-[1rem]">
      <h1>{{ headerTitle() }}</h1>
      @if (!isNew()) {
        <p-tag [severity]="statusSeverity(account()?.status)" [value]="statusLabel(account()?.status)" />
      }
    </div>
    <div class="buttons">
      <span class="separator"></span>
      @if (!isNew()) {
        <p-speedDial [model]="transactionSpeedDialItems" direction="left" [transitionDelay]="80">
          <ng-template #button let-toggleCallback="toggleCallback">
            <p-button (click)="toggleCallback($event)" [label]="'BankAccount_Txn_New' | translateWF: 'New transaction'" icon="pi pi-plus" />
          </ng-template>
          <ng-template #item let-item let-toggleCallback="toggleCallback">
            <p-button variant="outlined" severity="secondary" (click)="toggleCallback($event, item)" [label]="item.label" [icon]="item.icon" />
          </ng-template>
        </p-speedDial>
        <p-button
          [label]="'BankAccount_Txn_Transfer' | translateWF: 'Transfer to another account'"
          icon="pi pi-arrow-right-arrow-left"
          variant="outlined"
          severity="secondary"
          (click)="startInternalTransfer()"
        />
      }
      <p-button [label]="'Back' | translateWF: 'Back'" icon="pi pi-arrow-left" variant="outlined" (click)="back()" />
    </div>
  </header>

  <div class="content">
    @if (!isNew()) {
      <!-- ============ HEADER CARD ============ -->
      <div class="account-header">
        <div class="account-header__main">
          <div class="account-header__icon"><i class="pi pi-building"></i></div>
          <div>
            <p class="account-header__subtitle">{{ account()?.code }} · {{ account()?.bankName }} · {{ typeLabel(account()?.type) }}</p>
            <p class="account-header__subtitle">
              {{ 'BankAccount_Number' | translateWF: 'Acct' }} <span class="account-header__acct">{{ account()?.bankAccountNumber }}</span> ·
              {{ currencyLabel(account()?.currency) }}
            </p>
          </div>
        </div>
        <div class="account-header__balance">
          <span class="account-header__balance-label">{{ 'BankAccount_CurrentBalance' | translateWF: 'Current balance' }}</span>
          <span class="account-header__balance-value" [class.balance--negative]="(currentBalance() ?? 0) < 0" [class.balance--masked]="!canViewBalance()">
            {{ currentBalance() | maskedAmount }}
            @if (canViewBalance()) {
              <span>{{ currencyLabel(account()?.currency) }}</span>
            }
          </span>
          <span class="account-header__balance-asof">{{ 'BankAccount_AsOf' | translateWF: 'as of' }} {{ asOfDate() | date: 'dd/MM/yyyy' }}</span>
        </div>
      </div>

      <!-- ============ TAB STRIP ============ -->
      <!-- Tab list only: the panels below stay as @if blocks so `setTab` keeps deferring the
           full-history fetch until the Transactions tab is actually opened. -->
      <p-tabs [value]="activeTab()" (valueChange)="setTab($any($event))">
        <p-tablist>
          <p-tab value="overview"> <i class="pi pi-chart-bar"></i> {{ 'BankAccount_Tab_Overview' | translateWF: 'Overview' }} </p-tab>
          <p-tab value="details"> <i class="pi pi-id-card"></i> {{ 'BankAccount_Tab_Details' | translateWF: 'Details' }} </p-tab>
          <p-tab value="transactions"> <i class="pi pi-list"></i> {{ 'BankAccount_Tab_Transactions' | translateWF: 'Transactions' }} </p-tab>
          <p-tab value="drafts">
            <i class="pi pi-file-edit"></i> {{ 'BankAccount_Tab_Drafts' | translateWF: 'Drafts' }}
            @if (draftCount() > 0) {
              <p-tag [value]="draftCount().toString()" severity="warn" />
            }
          </p-tab>
          @if (isOwner()) {
            <p-tab value="access"> <i class="pi pi-users"></i> {{ 'BankAccount_Tab_Access' | translateWF: 'Access' }} </p-tab>
          }
        </p-tablist>
      </p-tabs>
    }

    <!-- ======================= OVERVIEW ======================= -->
    @if (!isNew() && activeTab() === 'overview') {
      @if (isBelowMinimum()) {
        <div class="alert-banner">
          <i class="pi pi-exclamation-triangle"></i>
          <span>
            {{ 'BankAccount_BelowBanner_1' | translateWF: 'Balance is' }} <strong>{{ belowMinimumBy() | maskedAmount }} {{ currencyLabel(account()?.currency) }}</strong>
            {{ 'BankAccount_BelowBanner_2' | translateWF: 'below the minimum threshold of' }} {{ minimumBalance() | maskedAmount }} {{ currencyLabel(account()?.currency) }}.
          </span>
        </div>
      }

      <!-- KPI cards -->
      <div class="cs-summary-grid">
        <p-card data-variant="summary" [header]="'BankAccount_CurrentBalance' | translateWF: 'Current balance'">
          <ng-template pTemplate="subtitle">
            <span [class.balance--negative]="(currentBalance() ?? 0) < 0" [class.balance--masked]="!canViewBalance()"
              >{{ currentBalance() | maskedAmount }}
              @if (canViewBalance()) {
                <span>{{ currencyLabel(account()?.currency) }}</span>
              }
            </span>
          </ng-template>
          <div>{{ 'BankAccount_AsOf' | translateWF: 'as of' }} {{ asOfDate() | date: 'dd/MM/yyyy' }}</div>
        </p-card>
        <p-card data-variant="summary" data-severity="success" [header]="('BankAccount_Inflow' | translateWF: 'Inflow') + ' · ' + monthLabel()">
          <ng-template pTemplate="subtitle">{{ inflow() | number: '1.2-2' }}</ng-template>
          <div>{{ inflowCount() }} {{ 'BankAccount_Deposits' | translateWF: 'deposits' }}</div>
        </p-card>
        <p-card data-variant="summary" [header]="('BankAccount_Outflow' | translateWF: 'Outflow') + ' · ' + monthLabel()">
          <ng-template pTemplate="subtitle">{{ outflow() | number: '1.2-2' }}</ng-template>
          <div>{{ outflowCount() }} {{ 'BankAccount_Payments' | translateWF: 'payments' }}</div>
        </p-card>
        <p-card data-variant="summary" [header]="('BankAccount_Net' | translateWF: 'Net') + ' · ' + monthLabel()">
          <ng-template pTemplate="subtitle">
            <span [class.balance--negative]="net() < 0">{{ net() | number: '1.2-2' }}</span>
          </ng-template>
          <div>{{ 'BankAccount_MonthToDate' | translateWF: 'month to date' }}</div>
        </p-card>
      </div>

      <div class="grid-12 gap-[1.5rem]">
        <!-- Recent transactions -->
        <div class="span-12-12-8">
          <p-panel [header]="'BankAccount_RecentTransactions' | translateWF: 'Recent transactions'">
            <ng-template pTemplate="icons">
              <a class="panel-card__link" (click)="setTab('transactions')">{{ 'BankAccount_ViewAll' | translateWF: 'View all' }} <i class="pi pi-angle-right"></i></a>
            </ng-template>
            <p-card>
              @for (line of recentLines(); track line.transactionId) {
                <div class="txn-row">
                  <div class="txn-row__icon"><i class="pi pi-receipt"></i></div>
                  <div class="txn-row__main">
                    <span class="txn-row__concept">
                      {{ line.concept || line.description || movementTypeLabel(line.movementType) }}
                      @if (line.isCharge) {
                        <p-tag severity="secondary" styleClass="ml-2" [value]="'BankAccount_Statement_Charge' | translateWF: 'Charge'" />
                      }
                    </span>
                    <span class="txn-row__meta">{{ line.postingDate | date: 'dd/MM/yyyy' }} · {{ line.number }}</span>
                  </div>
                  <div class="txn-row__amounts">
                    <span class="txn-row__amount" [class.balance--negative]="signedAmount(line) < 0" [class.txn-row__amount--in]="signedAmount(line) > 0">
                      {{ signedAmount(line) > 0 ? '+' : '' }}{{ signedAmount(line) | number: '1.2-2' }}
                    </span>
                    <span class="txn-row__running">{{ line.runningBalance | number: '1.2-2' }} {{ currencyLabel(account()?.currency) }}</span>
                  </div>
                </div>
              } @empty {
                <p class="panel-card__empty">{{ 'BankAccount_NoTransactions' | translateWF: 'No transactions yet.' }}</p>
              }
            </p-card>
          </p-panel>
        </div>

        <!-- Thresholds + snapshot -->
        <div class="span-12-12-4 col">
          <p-panel [header]="'BankAccount_BalanceVsThresholds' | translateWF: 'Balance vs. thresholds'">
            <p-card>
              @if (thresholdPercent() !== null) {
                <div class="threshold-bar"><div class="threshold-bar__fill" [class.threshold-bar__fill--alert]="isBelowMinimum()" [style.width.%]="thresholdPercent()"></div></div>
              } @else {
                <!-- No bar at all rather than an empty one: a 0%-filled track reads as "no money here". -->
                <div class="threshold-bar threshold-bar--unavailable"></div>
              }
              <div class="threshold-bar__labels">
                <span>{{ 'BankAccount_Min' | translateWF: 'Min' }} {{ minimumBalance() | maskedAmount }}</span>
                <span>{{ 'BankAccount_Max' | translateWF: 'Max' }} {{ maximumBalance() | maskedAmount }}</span>
              </div>
              @if (isBelowMinimum()) {
                <p class="threshold-note threshold-note--alert">
                  {{ 'BankAccount_BelowMinimumBy' | translateWF: 'Below minimum by' }} {{ belowMinimumBy() | maskedAmount }} {{ currencyLabel(account()?.currency) }}
                </p>
              }
            </p-card>
          </p-panel>

          <p-panel [header]="'BankAccount_Snapshot' | translateWF: 'Account snapshot'">
            <p-card>
              <dl class="snapshot">
                <div>
                  <dt>{{ 'BankAccount_Bank' | translateWF: 'Bank' }}</dt>
                  <dd>{{ account()?.bankName }}</dd>
                </div>
                <div>
                  <dt>{{ 'BankAccount_AccountNumber' | translateWF: 'Account number' }}</dt>
                  <dd>{{ account()?.bankAccountNumber }}</dd>
                </div>
                <div>
                  <dt>{{ 'BankAccount_SwiftBic' | translateWF: 'SWIFT / BIC' }}</dt>
                  <dd>{{ account()?.swiftBicCode || '—' }}</dd>
                </div>
                <div>
                  <dt>{{ 'BankAccount_Table_Currency' | translateWF: 'Currency' }}</dt>
                  <dd>{{ currencyLabel(account()?.currency) }}</dd>
                </div>
                <div>
                  <dt>{{ 'BankAccount_GlAccount' | translateWF: 'GL account' }}</dt>
                  <dd>{{ account()?.glAccountNumber || '—' }}</dd>
                </div>
                <div>
                  <dt>{{ 'BankAccount_AccountHolder' | translateWF: 'Account holder' }}</dt>
                  <dd>{{ account()?.accountHolderName }}</dd>
                </div>
              </dl>
            </p-card>
          </p-panel>
        </div>
      </div>
    }

    <!-- ======================= DETAILS (form) ======================= -->
    @if (isNew() || activeTab() === 'details') {
      <div class="details-toolbar">
        <span class="details-hint">
          <i class="pi pi-info-circle"></i>
          {{ 'BankAccount_Details_Hint' | translateWF: 'Editing saves account settings only — balances and transactions are managed separately.' }}
        </span>
        <p-button [label]="'BankAccount_SaveDetails' | translateWF: 'Save details'" icon="pi pi-save" (click)="save()" />
      </div>

      <form [formGroup]="bankAccountForm" class="col">
        <!-- General Information -->
        <p-panel [header]="'BankAccount_Section_General' | translateWF: 'General Information'" [toggleable]="false">
          <p-card>
            <div class="grid-12">
              <div class="span-12-3">
                <div class="form-box">
                  <label for="code">{{ fieldLabels.code | translateWF: 'Code' }}</label>
                  <input id="code" type="text" formControlName="code" pInputText class="w-full" />
                </div>
              </div>
              <div class="span-12-3">
                <div class="form-box">
                  <label for="ownerUsername">{{ fieldLabels.ownerUsername | translateWF: 'Owner' }}</label>
                  <p-select
                    inputId="ownerUsername"
                    formControlName="ownerUsername"
                    [options]="assignableUsers()"
                    optionLabel="displayName"
                    optionValue="username"
                    [filter]="true"
                    filterBy="displayName,username"
                    [showClear]="false"
                    appendTo="body"
                    styleClass="w-full"
                    [placeholder]="'BankAccount_Details_OwnerPlaceholder' | translateWF: 'Select an owner'"
                    [ngClass]="{
                      'ng-invalid ng-dirty': bankAccountForm.controls.ownerUsername.invalid && bankAccountForm.controls.ownerUsername.touched,
                    }"
                  />
                  <small class="form-hint">
                    {{
                      isNew()
                        ? ('BankAccount_Details_OwnerHintNew' | translateWF: 'The owner administers this account and decides who else may reach it.')
                        : ('BankAccount_Details_OwnerHintExisting' | translateWF: 'Ownership is transferred from the Access tab, by the owner.')
                    }}
                  </small>
                </div>
              </div>
              <div class="span-12-3">
                <div class="form-box">
                  <label for="name">{{ fieldLabels.name | translateWF: 'Name' }}</label>
                  <input
                    id="name"
                    type="text"
                    formControlName="name"
                    pInputText
                    class="w-full"
                    [ngClass]="{ 'ng-invalid ng-dirty': bankAccountForm.controls.name.invalid && bankAccountForm.controls.name.touched }"
                  />
                </div>
              </div>
              <div class="span-12-3">
                <div class="form-box">
                  <label for="type">{{ fieldLabels.type | translateWF: 'Type' }}</label>
                  <p-select
                    id="type"
                    [options]="typeOptions()"
                    formControlName="type"
                    optionLabel="label"
                    optionValue="value"
                    appendTo="body"
                    styleClass="w-full"
                    [ngClass]="{ 'ng-invalid ng-dirty': bankAccountForm.controls.type.invalid && bankAccountForm.controls.type.touched }"
                  />
                </div>
              </div>
              <div class="span-12-3">
                <div class="form-box">
                  <label for="currency">{{ fieldLabels.currency | translateWF: 'Currency' }}</label>
                  <p-select
                    id="currency"
                    [options]="currencyOptions()"
                    formControlName="currency"
                    optionLabel="label"
                    optionValue="value"
                    appendTo="body"
                    styleClass="w-full"
                    [readonly]="currencyLocked()"
                    [ngClass]="{ 'ng-invalid ng-dirty': bankAccountForm.controls.currency.invalid && bankAccountForm.controls.currency.touched }"
                  />
                  @if (currencyLocked()) {
                    <!-- A field that refuses to open reads as broken unless it says why. -->
                    <small class="block mt-1 text-xs text-surface-500">
                      {{ 'BankAccount_Currency_Locked' | translateWF: 'Fixed once saved — balances and transactions are held in this currency.' }}
                    </small>
                  }
                </div>
              </div>
              <div class="span-12-3">
                <div class="form-box">
                  <label for="status">{{ fieldLabels.status | translateWF: 'Status' }}</label>
                  <p-select
                    id="status"
                    [options]="statusOptions()"
                    formControlName="status"
                    optionLabel="label"
                    optionValue="value"
                    appendTo="body"
                    styleClass="w-full"
                    [ngClass]="{ 'ng-invalid ng-dirty': bankAccountForm.controls.status.invalid && bankAccountForm.controls.status.touched }"
                  />
                </div>
              </div>
              <div class="span-12-3">
                <div class="form-box">
                  <label for="country">{{ fieldLabels.country | translateWF: 'Country' }}</label>
                  <input
                    id="country"
                    type="text"
                    formControlName="country"
                    pInputText
                    class="w-full"
                    [ngClass]="{ 'ng-invalid ng-dirty': bankAccountForm.controls.country.invalid && bankAccountForm.controls.country.touched }"
                  />
                </div>
              </div>
              <div class="span-12-3">
                <div class="form-box">
                  <label for="accountHolderName">{{ fieldLabels.accountHolderName | translateWF: 'Holder Name' }}</label>
                  <input
                    id="accountHolderName"
                    type="text"
                    formControlName="accountHolderName"
                    pInputText
                    class="w-full"
                    [ngClass]="{ 'ng-invalid ng-dirty': bankAccountForm.controls.accountHolderName.invalid && bankAccountForm.controls.accountHolderName.touched }"
                  />
                </div>
              </div>
              <div class="span-12-3">
                <div class="form-box">
                  <label for="holderPhoneNumber">{{ fieldLabels.holderPhoneNumber | translateWF: 'Holder Phone' }}</label>
                  <input id="holderPhoneNumber" type="text" formControlName="holderPhoneNumber" pInputText class="w-full" />
                </div>
              </div>
              <div class="span-12">
                <div class="form-box">
                  <label for="description">{{ fieldLabels.description | translateWF: 'Description' }}</label>
                  <input
                    id="description"
                    type="text"
                    formControlName="description"
                    pInputText
                    class="w-full"
                    [placeholder]="'BankAccount_Description_Placeholder' | translateWF: 'Optional notes about this account'"
                  />
                </div>
              </div>
            </div>
          </p-card>
        </p-panel>

        <!-- Banking Information -->
        <p-panel [header]="'BankAccount_Section_Banking' | translateWF: 'Banking Information'" [toggleable]="false">
          <p-card>
            <div class="grid-12">
              <div class="span-12-3">
                <div class="form-box">
                  <label for="bankName">{{ fieldLabels.bankName | translateWF: 'Bank Name' }}</label>
                  <input
                    id="bankName"
                    type="text"
                    formControlName="bankName"
                    pInputText
                    class="w-full"
                    [ngClass]="{ 'ng-invalid ng-dirty': bankAccountForm.controls.bankName.invalid && bankAccountForm.controls.bankName.touched }"
                  />
                </div>
              </div>
              <div class="span-12-3">
                <div class="form-box">
                  <label for="bankAccountNumber">{{ fieldLabels.bankAccountNumber | translateWF: 'Account Number' }}</label>
                  <input
                    id="bankAccountNumber"
                    type="text"
                    formControlName="bankAccountNumber"
                    pInputText
                    class="w-full"
                    [ngClass]="{ 'ng-invalid ng-dirty': bankAccountForm.controls.bankAccountNumber.invalid && bankAccountForm.controls.bankAccountNumber.touched }"
                  />
                </div>
              </div>
              <div class="span-12-3">
                <div class="form-box">
                  <label for="swiftBicCode">{{ fieldLabels.swiftBicCode | translateWF: 'SWIFT/BIC' }}</label>
                  <input id="swiftBicCode" type="text" formControlName="swiftBicCode" pInputText class="w-full" />
                </div>
              </div>
              <div class="span-12-3">
                <div class="form-box">
                  <label for="ibanCode">{{ fieldLabels.ibanCode | translateWF: 'IBAN' }}</label>
                  <input id="ibanCode" type="text" formControlName="ibanCode" pInputText class="w-full" />
                </div>
              </div>
              <div class="span-12-6">
                <div class="form-box">
                  <app-financial-account-select
                    [label]="fieldLabels.glAccountReferenceId | translateWF: 'GL Account'"
                    [referenceControl]="bankAccountForm.controls.glAccountReferenceId"
                    [numberControl]="bankAccountForm.controls.glAccountNumber"
                    [nameControl]="glAccountNameControl"
                    [subledgerType]="SubledgerType.Bank"
                    [unlinkedBankOnly]="true"
                    [postingOnly]="false"
                    [readonly]="glAccountLocked()"
                  />
                  @if (glAccountLocked()) {
                    <!-- A field that refuses to open reads as broken unless it says why. -->
                    <small class="block mt-1 text-xs text-surface-500">
                      {{ 'BankAccount_GlAccount_Locked' | translateWF: 'Fixed once saved — transactions already post to this account.' }}
                    </small>
                  }
                </div>
              </div>
            </div>
          </p-card>
        </p-panel>

        <!-- Balances -->
        <p-panel [header]="'BankAccount_Section_Balances' | translateWF: 'Balances'" [toggleable]="false">
          <p-card>
            <div class="grid-12">
              <div class="span-12-3">
                <div class="form-box">
                  <label for="currentBalance">{{ fieldLabels.currentBalance | translateWF: 'Current Balance' }}</label>
                  <p-inputNumber id="currentBalance" formControlName="currentBalance" mode="decimal" [minFractionDigits]="2" [maxFractionDigits]="2" styleClass="w-full" />
                </div>
              </div>
              <div class="span-12-3">
                <div class="form-box">
                  <label for="minimumBalance">{{ fieldLabels.minimumBalance | translateWF: 'Minimum Balance' }}</label>
                  <p-inputNumber id="minimumBalance" formControlName="minimumBalance" mode="decimal" [minFractionDigits]="2" [maxFractionDigits]="2" styleClass="w-full" />
                </div>
              </div>
              <div class="span-12-3">
                <div class="form-box">
                  <label for="maximumBalance">{{ fieldLabels.maximumBalance | translateWF: 'Maximum Balance' }}</label>
                  <p-inputNumber id="maximumBalance" formControlName="maximumBalance" mode="decimal" [minFractionDigits]="2" [maxFractionDigits]="2" styleClass="w-full" />
                </div>
              </div>
            </div>
          </p-card>
        </p-panel>
      </form>
    }

    <!-- ======================= TRANSACTIONS ======================= -->
    @if (!isNew() && activeTab() === 'transactions') {
      <div class="txn-toolbar">
        <span class="p-input-icon-left txn-toolbar__search">
          <i class="pi pi-search"></i>
          <input type="text" pInputText [formControl]="txnSearchControl" [placeholder]="'BankAccount_Txn_Search' | translateWF: 'Search reference or description'" class="w-full" />
        </span>
        <p-select
          [options]="movementTypeOptions()"
          [formControl]="txnTypeControl"
          optionLabel="label"
          optionValue="value"
          [showClear]="true"
          appendTo="body"
          [placeholder]="'Type' | translateWF: 'Type'"
        />
        <p-datepicker
          [formControl]="txnDateRangeControl"
          selectionMode="range"
          [readonlyInput]="true"
          [showIcon]="true"
          appendTo="body"
          [placeholder]="'BankAccount_Txn_DateRange' | translateWF: 'Date range'"
          styleClass="txn-toolbar__range"
        />
        <span class="txn-toolbar__spacer"></span>
        <!-- Off by default: a voided movement is one that was recorded and then retired, and the bank's own
             statement will never show it. Shown on request, for someone looking for a movement that has
             disappeared. Only offered when there is something to reveal. -->
        @if (voidedCount() > 0) {
          <p-button
            [label]="
              (showVoided() ? ('BankAccount_Txn_HideVoided' | translateWF: 'Hide voided') : ('BankAccount_Txn_ShowVoided' | translateWF: 'Show voided')) +
              ' (' +
              voidedCount() +
              ')'
            "
            [icon]="showVoided() ? 'pi pi-eye-slash' : 'pi pi-eye'"
            variant="outlined"
            severity="secondary"
            (click)="toggleVoided()"
          />
        }
        <!-- Exports the toolbar's date range, re-queried from the API so the workbook is built where every
             other export in this app is built. The range goes as two calendar DAYS: posting dates are UTC
             instants and Honduras is UTC-6, so an 18:30 movement is stored on the following UTC day and a
             boundary decided here would already have lost it. Disabled only when there is nothing loaded at
             all — the search and type filters narrow the table, not the file. -->
        <p-button
          [label]="'Export' | translateWF: 'Export'"
          icon="pi pi-download"
          variant="outlined"
          severity="secondary"
          [loading]="isExporting()"
          [disabled]="transactionLines().length === 0"
          (click)="exportTransactions()"
        />
      </div>

      <p-panel [header]="'BankAccount_Tab_Transactions' | translateWF: 'Transactions'">
        <p-card data-variant="compact">
          <p-table [value]="filteredLines()" [paginator]="true" [rows]="25" [rowsPerPageOptions]="[25, 50, 100]" [rowHover]="true">
            <ng-template pTemplate="header">
              <tr class="primary__header">
                <th>{{ 'BankAccount_Txn_Date' | translateWF: 'Date' }}</th>
                <th>{{ 'BankAccount_Txn_Reference' | translateWF: 'Reference' }}</th>
                <th>{{ 'BankAccount_Txn_Description' | translateWF: 'Description' }}</th>
                <th>{{ 'Type' | translateWF: 'Type' }}</th>
                <th class="text-right">{{ 'BankAccount_Txn_Amount' | translateWF: 'Amount' }}</th>
                <th class="text-right">{{ 'BankAccount_Txn_Balance' | translateWF: 'Balance' }}</th>
                <th>{{ 'Status' | translateWF: 'Status' }}</th>
              </tr>
            </ng-template>

            <ng-template pTemplate="body" let-line>
              <tr [class.txn-row--voided]="line.isVoided">
                <td>{{ line.postingDate | date: 'dd/MM/yyyy' }}</td>
                <td>
                  <!-- A real link, not a click handler: the statement is where someone goes looking for a
                   movement, and following one should not cost them the list they found it in. routerLink
                   with target rather than window.open, so the href is real — middle-click, ctrl-click and
                   the context menu all behave, and hovering shows where it goes. -->
                  <a class="cursor-pointer" [routerLink]="['/bank-transaction-details']" [queryParams]="{ id: line.transactionId }" target="_blank" rel="noopener">{{
                    line.number
                  }}</a>
                </td>
                <td>{{ line.concept || line.description }}</td>
                <td>
                  @if (line.isCharge) {
                    <p-tag severity="secondary" [value]="'BankAccount_Statement_Charge' | translateWF: 'Charge'" />
                  } @else {
                    <p-tag [severity]="movementSeverity(line.movementType)" [value]="movementTypeLabel(line.movementType)" />
                  }
                </td>
                <!-- A voided line still states its figure — the question being asked is what the movement WAS —
                 but struck through, because none of it is in the balance. -->
                <td
                  class="text-right"
                  [class.txn-amount--voided]="line.isVoided"
                  [class.balance--negative]="!line.isVoided && signedAmount(line) < 0"
                  [class.amount--in]="!line.isVoided && signedAmount(line) > 0"
                >
                  {{ signedAmount(line) > 0 ? '+' : '' }}{{ signedAmount(line) | number: '1.2-2' }}
                </td>
                <!-- Never a running balance on a void: it backs out exactly what it applied, so the rows around
                 it carry the same total either way. A figure here would invite adding it up. -->
                <td class="text-right" [class.balance--negative]="!line.isVoided && line.runningBalance < 0">
                  @if (line.isVoided) {
                    <span class="text-surface-500">—</span>
                  } @else {
                    {{ line.runningBalance | number: '1.2-2' }} {{ currencyLabel(account()?.currency) }}
                  }
                </td>
                <td>
                  @if (line.isVoided) {
                    <p-tag severity="danger" [value]="'BankTransaction_Status_Void' | translateWF: 'Voided'" />
                  } @else {
                    <p-tag severity="success" [value]="'BankTransaction_Status_Posted' | translateWF: 'Posted'" />
                  }
                </td>
              </tr>
            </ng-template>

            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="7">{{ 'BankAccount_NoTransactions' | translateWF: 'No transactions found' }}</td>
              </tr>
            </ng-template>
          </p-table>
        </p-card>
      </p-panel>
    }

    <!-- Drafts: movements started but never posted. They move no money and never reach the statement,
         so this tab is the only place they are visible at all. -->
    <!-- ======================= ACCESS ======================= -->
    @if (!isNew() && activeTab() === 'access' && isOwner()) {
      <div class="details-toolbar">
        <span class="details-hint">
          <i class="pi pi-info-circle"></i>
          {{
            'BankAccount_Access_Hint' | translateWF: 'You own this account. Anyone not listed here can see the account but never its balance — including in the chart of accounts.'
          }}
        </span>
        <p-button
          [label]="'BankAccount_Access_Save' | translateWF: 'Save access'"
          icon="pi pi-save"
          [loading]="savingAccess()"
          [disabled]="accessForm.pristine"
          (click)="saveAccess()"
        />
      </div>

      <p-confirmDialog />

      <form [formGroup]="accessForm" class="col">
        <!-- Owner -->
        <p-panel [header]="'BankAccount_Access_OwnerPanel' | translateWF: 'Owner'" [toggleable]="false">
          <p-card>
            <div class="grid-12">
              <div class="span-12-4">
                <div class="form-box">
                  <label for="accessOwner">{{ 'BankAccount_Access_Owner' | translateWF: 'Account owner' }}</label>
                  <p-select
                    inputId="accessOwner"
                    formControlName="ownerUsername"
                    [options]="assignableUsers()"
                    optionLabel="displayName"
                    optionValue="username"
                    [filter]="true"
                    filterBy="displayName,username"
                    appendTo="body"
                    styleClass="w-full"
                    (onChange)="onOwnerSelected($any($event).value)"
                    [ngClass]="{
                      'ng-invalid ng-dirty': accessForm.controls.ownerUsername.invalid && accessForm.controls.ownerUsername.touched,
                    }"
                  />
                  <small class="form-hint">
                    {{ 'BankAccount_Access_OwnerHint' | translateWF: 'The owner edits this account and decides who else may reach it.' }}
                  </small>
                </div>
              </div>

              @if (isTransferringOwnership()) {
                <div class="span-12-8">
                  <p-message severity="warn" styleClass="w-full">
                    <div class="col-sm">
                      <span>
                        @if (transferLosesOwnAccess()) {
                          {{
                            'BankAccount_Access_TransferWarnLosing'
                              | translateWF
                                : 'Saving this hands the account over. You will lose ownership and all access to it — including its balance — and only the new owner will be able to give it back.'
                          }}
                        } @else {
                          {{
                            'BankAccount_Access_TransferWarnKeeping'
                              | translateWF
                                : 'Saving this hands the account over. You keep the access listed below, but only the new owner will be able to change ownership from then on.'
                          }}
                        }
                      </span>
                      @if (transferLosesOwnAccess()) {
                        <!-- Offered here because this is the last moment the outgoing owner can grant it
                           to themselves — afterwards they no longer have the authority to. -->
                        <div>
                          <p-button
                            [label]="'BankAccount_Access_KeepMyAccess' | translateWF: 'Keep my access as an employee'"
                            icon="pi pi-user-plus"
                            severity="secondary"
                            [text]="true"
                            (click)="keepOutgoingOwnerAccess()"
                          />
                        </div>
                      }
                    </div>
                  </p-message>
                </div>
              }
            </div>
          </p-card>
        </p-panel>

        <p-panel [header]="'BankAccount_Access_Panel' | translateWF: 'Employee access'" [toggleable]="false">
          <p-card data-variant="compact">
            <div class="grid-12">
              <div class="span-12">
                <p-table [value]="employeeRows()" styleClass="p-datatable-sm">
                  <ng-template pTemplate="header">
                    <tr>
                      <th style="width: 32%">{{ 'BankAccount_Access_Employee' | translateWF: 'Employee' }}</th>
                      <th>{{ 'BankAccount_Access_Rights' | translateWF: 'Permissions' }}</th>
                      <th style="width: 4rem"></th>
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="body" let-row let-i="rowIndex">
                    <tr [formGroup]="row">
                      <td>
                        <!-- Per-row options: this row's own selection must stay in its own list, or
                           p-select cannot resolve the label and renders blank. -->
                        <p-select
                          formControlName="username"
                          [options]="usersForRow(row.controls.username.value)"
                          optionLabel="displayName"
                          optionValue="username"
                          [filter]="true"
                          filterBy="displayName,username"
                          [editable]="false"
                          appendTo="body"
                          styleClass="w-full"
                          (onChange)="onEmployeeSelected()"
                          [placeholder]="'BankAccount_Access_PickUserPlaceholder' | translateWF: 'Select a user'"
                          [ngClass]="{ 'ng-invalid ng-dirty': row.controls.username.invalid && row.controls.username.touched }"
                        />
                      </td>
                      <td>
                        <p-multiSelect
                          formControlName="access"
                          [options]="accessRightOptions"
                          optionLabel="label"
                          optionValue="value"
                          display="chip"
                          appendTo="body"
                          styleClass="w-full"
                          [placeholder]="'BankAccount_Access_NoRights' | translateWF: 'No permissions — listed but cannot act'"
                        />
                      </td>
                      <td class="text-right">
                        <p-button icon="pi pi-trash" severity="danger" [text]="true" (click)="removeEmployeeRow(i)" />
                      </td>
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="emptymessage">
                    <tr>
                      <td colspan="3" class="text-center">
                        {{ 'BankAccount_Access_Empty' | translateWF: 'Only you can see this account’s balance. Add an employee to share it.' }}
                      </td>
                    </tr>
                  </ng-template>
                </p-table>
              </div>
              <div class="span-12">
                <p-button
                  [label]="'BankAccount_Access_Add' | translateWF: 'Add employee'"
                  icon="pi pi-plus"
                  [text]="true"
                  [disabled]="availableUsers().length === 0"
                  (click)="addEmployeeRow()"
                />
              </div>
            </div>
          </p-card>
        </p-panel>
      </form>
    }

    @if (!isNew() && activeTab() === 'drafts') {
      <div class="txn-toolbar">
        <span class="p-input-icon-left txn-toolbar__search">
          <i class="pi pi-search"></i>
          <input
            type="text"
            pInputText
            [formControl]="draftSearchControl"
            [placeholder]="'BankAccount_Draft_Search' | translateWF: 'Search number, reference or description'"
            class="w-full"
          />
        </span>
      </div>

      <p-panel [header]="'BankAccount_Tab_Drafts' | translateWF: 'Drafts'">
        <p-card data-variant="compact">
          <p-table [value]="filteredDrafts()" [paginator]="true" [rows]="25" [rowsPerPageOptions]="[25, 50, 100]" [rowHover]="true">
            <ng-template pTemplate="header">
              <tr class="primary__header">
                <th>{{ 'BankAccount_Txn_Date' | translateWF: 'Date' }}</th>
                <th>{{ 'BankAccount_Txn_Number' | translateWF: 'Number' }}</th>
                <th>{{ 'BankAccount_Txn_Reference' | translateWF: 'Reference' }}</th>
                <th>{{ 'BankAccount_Txn_Description' | translateWF: 'Description' }}</th>
                <th>{{ 'Type' | translateWF: 'Type' }}</th>
                <th class="text-right">{{ 'BankAccount_Txn_Amount' | translateWF: 'Amount' }}</th>
                <th></th>
              </tr>
            </ng-template>

            <ng-template pTemplate="body" let-draft>
              <tr>
                <td>{{ draft.transactionDate | date: 'dd/MM/yyyy' }}</td>
                <td>
                  <a class="cursor-pointer" (click)="openDraft(draft)">{{ draft.number }}</a>
                </td>
                <td>{{ draft.bankingReference || '—' }}</td>
                <td>
                  {{ draft.concept || draft.description || '—' }}
                  <!-- Says why an otherwise blank draft exists: it belongs to a document, not to someone who
                   started a movement and wandered off. -->
                  @if (draft.ownerKey?.number) {
                    <p-tag severity="secondary" [value]="draft.ownerKey.number" icon="pi pi-link" styleClass="ml-2" />
                  }
                </td>
                <td>
                  <p-tag [severity]="movementSeverity(draft.financialTransactionType)" [value]="movementTypeLabel(draft.financialTransactionType)" />
                </td>
                <td class="text-right" [class.balance--negative]="draftAmount(draft) < 0" [class.amount--in]="draftAmount(draft) > 0">
                  {{ draftAmount(draft) > 0 ? '+' : '' }}{{ draftAmount(draft) | number: '1.2-2' }} {{ currencyLabel(account()?.currency) }}
                </td>
                <td class="text-right">
                  <p-button [label]="'BankAccount_Draft_Open' | translateWF: 'Finish'" icon="pi pi-pencil" variant="outlined" size="small" (click)="openDraft(draft)" />
                </td>
              </tr>
            </ng-template>

            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="7">{{ 'BankAccount_NoDrafts' | translateWF: 'No draft transactions on this account' }}</td>
              </tr>
            </ng-template>
          </p-table>
        </p-card>
      </p-panel>
    }
  </div>
</main>
